import { useState, useEffect } from 'react';
import { realtimeService, RoomUser, GestureBroadcast, EmergencyAlert, ChatMessage } from '../../services/realtime.service';
import { useAppStore } from '../../store/appStore';
import './MultiplayerRoom.css';

export const MultiplayerRoom = () => {
  const { user, currentGesture } = useAppStore();
  const [roomId, setRoomId] = useState('');
  const [isInRoom, setIsInRoom] = useState(false);
  const [users, setUsers] = useState<RoomUser[]>([]);
  const [gestures, setGestures] = useState<GestureBroadcast[]>([]);
  const [emergencies, setEmergencies] = useState<EmergencyAlert[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [broadcastEnabled, setBroadcastEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (isInRoom) {
        realtimeService.leaveRoom();
      }
    };
  }, [isInRoom]);

  // Auto-broadcast gestures when enabled
  useEffect(() => {
    if (broadcastEnabled && isInRoom && currentGesture) {
      realtimeService.broadcastGesture(
        currentGesture.gesture,
        currentGesture.confidence
      ).catch(console.error);
    }
  }, [currentGesture, broadcastEnabled, isInRoom]);

  const handleJoinRoom = async () => {
    if (!roomId.trim()) {
      setError('Please enter a room ID');
      return;
    }

    if (!user) {
      setError('Please login first');
      return;
    }

    try {
      setError(null);
      await realtimeService.joinRoom(
        roomId,
        user.id,
        user.name || user.email,
        {
          onUserJoined: (newUser) => {
            console.log('User joined:', newUser);
          },
          onUserLeft: (userId) => {
            console.log('User left:', userId);
            setUsers(prev => prev.filter(u => u.id !== userId));
          },
          onGesture: (gesture) => {
            setGestures(prev => [...prev, gesture].slice(-50));
          },
          onEmergency: (alert) => {
            setEmergencies(prev => [...prev, alert].slice(-20));
            // Play alert sound
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGS57OihUBELTKXh8bllHAU2jdXvzn0pBSh+zPDajzsKElyx6OyrWBQLSKDf8sFuIwUrgc7y2Yk2CBhkuezooVARDEyl4fG5ZRwFNo3V7859KQUofsz');
            audio.play().catch(console.error);
          },
          onChat: (message) => {
            setChatMessages(prev => [...prev, message].slice(-100));
          },
          onPresenceUpdate: (updatedUsers) => {
            setUsers(updatedUsers);
          }
        }
      );
      setIsInRoom(true);
    } catch (err: any) {
      setError(err.message || 'Failed to join room');
      console.error('Join room error:', err);
    }
  };

  const handleLeaveRoom = async () => {
    try {
      await realtimeService.leaveRoom();
      setIsInRoom(false);
      setUsers([]);
      setGestures([]);
      setChatMessages([]);
      setRoomId('');
      setBroadcastEnabled(false);
    } catch (err) {
      console.error('Leave room error:', err);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    try {
      await realtimeService.sendChatMessage(chatInput);
      setChatInput('');
    } catch (err) {
      console.error('Send chat error:', err);
    }
  };

  const handleEmergencyAlert = async (type: 'distress' | 'fall' | 'help') => {
    try {
      await realtimeService.sendEmergencyAlert(type);
    } catch (err) {
      console.error('Emergency alert error:', err);
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  if (!user) {
    return (
      <div className="multiplayer-room">
        <div className="room-error">
          <h2>Login Required</h2>
          <p>Please login to use multiplayer features</p>
        </div>
      </div>
    );
  }

  return (
    <div className="multiplayer-room">
      <div className="room-header">
        <h2>🌐 Multiplayer Room</h2>
        <p>Real-time gesture collaboration and communication</p>
      </div>

      {!isInRoom ? (
        <div className="room-join">
          <div className="join-form">
            <input
              type="text"
              placeholder="Enter Room ID (e.g., team-alpha)"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleJoinRoom()}
            />
            <button onClick={handleJoinRoom} className="btn btn-primary">
              Join Room
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="room-info">
            <h3>How it works:</h3>
            <ul>
              <li>Enter a room ID to create or join a room</li>
              <li>Share the room ID with others to collaborate</li>
              <li>Broadcast your gestures in real-time</li>
              <li>Chat with other participants</li>
              <li>Send emergency alerts if needed</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="room-active">
          <div className="room-controls">
            <div className="room-id-display">
              <strong>Room:</strong> {roomId}
              <button onClick={() => navigator.clipboard.writeText(roomId)} className="btn-copy">
                📋 Copy
              </button>
            </div>
            <div className="broadcast-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={broadcastEnabled}
                  onChange={(e) => setBroadcastEnabled(e.target.checked)}
                />
                Auto-broadcast gestures
              </label>
            </div>
            <button onClick={handleLeaveRoom} className="btn btn-danger">
              Leave Room
            </button>
          </div>

          <div className="room-content">
            <div className="room-sidebar">
              <div className="users-panel">
                <h3>👥 Participants ({users.length})</h3>
                <div className="users-list">
                  {users.map(u => (
                    <div key={u.id} className="user-item">
                      <span className="user-status">🟢</span>
                      <span className="user-name">{u.name}</span>
                      {u.id === user.id && <span className="user-badge">You</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="emergency-panel">
                <h3>🚨 Emergency</h3>
                <div className="emergency-buttons">
                  <button 
                    onClick={() => handleEmergencyAlert('help')}
                    className="btn btn-warning"
                  >
                    Help
                  </button>
                  <button 
                    onClick={() => handleEmergencyAlert('distress')}
                    className="btn btn-danger"
                  >
                    Distress
                  </button>
                  <button 
                    onClick={() => handleEmergencyAlert('fall')}
                    className="btn btn-danger"
                  >
                    Fall
                  </button>
                </div>
                {emergencies.length > 0 && (
                  <div className="emergency-alerts">
                    <h4>Recent Alerts:</h4>
                    {emergencies.slice(-5).reverse().map((alert, i) => (
                      <div key={i} className="alert-item">
                        <strong>{alert.userName}</strong>: {alert.type}
                        <span className="alert-time">{formatTime(alert.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="room-main">
              <div className="gestures-feed">
                <h3>👋 Gesture Stream</h3>
                <div className="gestures-list">
                  {gestures.length === 0 ? (
                    <p className="empty-state">No gestures yet. Enable broadcasting to share yours!</p>
                  ) : (
                    gestures.slice(-20).reverse().map((g, i) => (
                      <div key={i} className="gesture-item">
                        <span className="gesture-user">{g.userName}</span>
                        <span className="gesture-name">{g.gesture}</span>
                        <span className="gesture-confidence">
                          {(g.confidence * 100).toFixed(0)}%
                        </span>
                        <span className="gesture-time">{formatTime(g.timestamp)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="chat-panel">
                <h3>💬 Chat</h3>
                <div className="chat-messages">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className="chat-message">
                      <strong>{msg.userName}:</strong> {msg.message}
                      <span className="chat-time">{formatTime(msg.timestamp)}</span>
                    </div>
                  ))}
                </div>
                <div className="chat-input">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                  />
                  <button onClick={handleSendChat} className="btn btn-primary">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
